import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Context from '../../../../utils/context';
import { isPhoneCallsEnabled } from '../../../../helpers/feature_checker.js';
import { mentorDeclinesCalls } from '../../../../helpers/mentor_calls';
import { isWithinCallHours, formatCallHoursRangeInViewerTimezone } from '../../../../helpers/timezone_utils';
import { createCallSuggestion } from '../../../../api/v2/calls';
import { trackPhoneCallClick } from '../../../../api/activities';
import { failToast } from '../../../ui_kit/utilities/fail_toast';

import CallActionButton from '../call_action_button';
import RequestCallModal from '../request_call_modal';
import ScheduleCallModal from '../schedule_call_modal';
import { displayCallIncentive } from '../../../phone_calls/incentive_banner';
import styles from '../new_conversation_room.module.css';

function AnimatedProfileHero({ name }) {
  const initials = name
    .trim()
    .split(' ')
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={styles.animated_profile_hero} aria-hidden='true'>
      <strong>{initials || 'PP'}</strong>
    </div>
  );
}

const CALL_MODAL_REQUEST = 'request';
const CALL_MODAL_SCHEDULE = 'schedule';

export default function PatientOtherUserPanel({ isMobile, otherUser, conversation, onCallCreated }) {
  const [heroImageFailed, setHeroImageFailed] = useState(false);
  const [callModal, setCallModal] = useState(null);
  const history = useHistory();
  const context = useContext(Context);
  const currentUser = context.auth.getCurrentUser();
  const currentAccount = currentUser.account;
  const profile = otherUser?.profile || {};
  const otherUserImageUrl = profile.photo || '';
  const displayName = profile.display_name || 'Community member';
  const conditionLabel = profile.focus_areas || otherUser?.match?.condition;
  const rawFirstName = displayName.split(' ')[0] || 'them';
  const firstName = rawFirstName.charAt(0).toUpperCase() + rawFirstName.slice(1);
  const otherUserTimezone = otherUser?.user?.timezone;
  const callHoursRange = formatCallHoursRangeInViewerTimezone(otherUserTimezone);
  const showHeroImage = profile.type === 'PatientPartner' && otherUserImageUrl && !heroImageFailed;
  const isPhoneFeatureEnabled = isPhoneCallsEnabled(currentAccount);
  const isMentor = currentUser?.current_profile_type === 'PatientPartner';
  const isAcceptingCalls = !!profile.accepts_calls;
  const showPhoneFeature = isPhoneFeatureEnabled && isAcceptingCalls && conversation;
  const showMentorDeclinesCalls = mentorDeclinesCalls({
    viewerProfileType: currentUser?.current_profile_type,
    phoneFeatureEnabled: isPhoneFeatureEnabled,
    profile,
  });
  const activeCall = conversation?.outgoing_call || conversation?.conversation_call || null;
  const callable = isWithinCallHours(otherUserTimezone);
  const requestDisabled = !showPhoneFeature || !callable || !!activeCall;
  const scheduleDisabled = !showPhoneFeature || !!activeCall;
  const callIncentive = isMentor ? null : displayCallIncentive(currentAccount, conversation);
  const closeCallModal = () => setCallModal(null);

  // Mentor-side "Suggest a call": gated on the mentor themself accepting
  // calls. A suggestion stays "open" until the patient creates a call after
  // it, so the button is disabled to avoid repeat suggestions (and repeat SMS).
  const [suggestingCall, setSuggestingCall] = useState(false);
  const mentorAcceptsCalls = !!currentUser?.current_profile?.accepts_calls;
  const showSuggestCall = isMentor && isPhoneFeatureEnabled && mentorAcceptsCalls && conversation;
  const showCallCard = isPhoneFeatureEnabled && conversation && (isMentor ? mentorAcceptsCalls : isAcceptingCalls);
  const conversationCalls = conversation?.calls || [];
  const callSuggestions = conversation?.call_suggestions || [];
  const latestSuggestion = callSuggestions.length > 0 ? callSuggestions[callSuggestions.length - 1] : null;
  const hasOpenSuggestion =
    !!latestSuggestion &&
    !conversationCalls.some((call) => new Date(call.created_at) >= new Date(latestSuggestion.created_at));
  const suggestDisabled = suggestingCall || hasOpenSuggestion || !!activeCall;

  const handleRequestCall = () => {
    trackPhoneCallClick('profile_panel_request_a_call', { conversation_id: conversation?.uuid });
    setCallModal(CALL_MODAL_REQUEST);
  };

  const handleScheduleCall = () => {
    trackPhoneCallClick('profile_panel_schedule_a_call', { conversation_id: conversation?.uuid });
    setCallModal(CALL_MODAL_SCHEDULE);
  };

  const handleSuggestCall = () => {
    if (suggestDisabled) return;
    trackPhoneCallClick('profile_panel_suggest_a_call', { conversation_id: conversation?.uuid });
    setSuggestingCall(true);
    createCallSuggestion(conversation.uuid)
      .then(() => {
        if (typeof onCallCreated === 'function') onCallCreated();
      })
      .catch((err) => failToast(err.message || 'Could not suggest a call. Please try again.'))
      .finally(() => setSuggestingCall(false));
  };

  const bioRef = useRef(null);
  const [showBioFade, setShowBioFade] = useState(false);

  const updateBioFade = useCallback(() => {
    const el = bioRef.current;
    if (!el) {
      return;
    }
    const hasMoreBelow = el.scrollHeight - el.scrollTop - el.clientHeight > 4;
    setShowBioFade(hasMoreBelow);
  }, []);

  useEffect(() => {
    updateBioFade();
  }, [profile.bio, updateBioFade]);

  const handleHowCallsWork = () => {
    trackPhoneCallClick('profile_panel_how_calls_work', { conversation_id: conversation?.uuid });
    const returnTo = conversation?.uuid ? `/member/conversations/${conversation.uuid}` : '/member/conversations';
    const role = isMentor ? 'mentor' : 'mentee';
    history.push(`/member/phone_calls/${role}?return_to=${encodeURIComponent(returnTo)}`);
  };

  const handleCallCreated = (call) => {
    closeCallModal();
    if (typeof onCallCreated === 'function') onCallCreated(call);
  };

  return (
    <div
      className={`${styles.detail_panel} ${styles.detail_profile_panel} ${isMobile ? styles.detail_panel_mobile : ''}`}
    >
      <section className={`${styles.detail_profile_card} ${showBioFade ? styles.detail_profile_card_faded : ''}`}>
        <div className={`${styles.detail_hero} ${styles.detail_profile_hero}`}>
          {showHeroImage ? (
            <>
              <img
                src={otherUserImageUrl}
                alt={displayName}
                draggable='false'
                onError={() => setHeroImageFailed(true)}
              />
              <span className={`${styles.detail_hero_scrim} ${styles.detail_profile_scrim}`} />
            </>
          ) : (
            <AnimatedProfileHero name={displayName} />
          )}
          <div className={`${styles.detail_hero_text} ${styles.detail_profile_hero_text}`}>
            <h3>{displayName}</h3>
            {conditionLabel && <p>{conditionLabel}</p>}
          </div>
        </div>

        {profile.bio && (
          <div className={styles.detail_profile_body} ref={bioRef} onScroll={updateBioFade}>
            <div className={styles.other_p_bio}>
              {profile.bio
                .split(/\n+/)
                .map((p) => p.trim())
                .filter(Boolean)
                .map((p, i) => (
                  <p key={i} style={{ marginTop: i === 0 ? 0 : '12px' }}>
                    {p}
                  </p>
                ))}
            </div>
          </div>
        )}
      </section>

      {showMentorDeclinesCalls && (
        <section className={styles.detail_call_card} aria-label='Does not accept phone calls'>
          <div className={`${styles.detail_card_header} ${styles.detail_call_header}`}>
            <p>Calls</p>
          </div>
          <div className={styles.detail_call_actions}>
            <div className={styles.mentor_calls_unavailable}>
              <span className={styles.mentor_calls_unavailable_icon} aria-hidden='true'>
                <svg width='16' height='16' viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'>
                  <defs>
                    <mask id='mentor-phone-slash'>
                      <rect width='256' height='256' fill='#fff' />
                      <line x1='40' y1='216' x2='216' y2='40' stroke='#000' strokeWidth='30' strokeLinecap='round' />
                    </mask>
                  </defs>
                  <path
                    d='M231.88,175.08A56.26,56.26,0,0,1,176,224C96.6,224,32,159.4,32,80A56.26,56.26,0,0,1,80.92,24.12a16,16,0,0,1,16.62,9.52l21.12,47.15,0,.12A16,16,0,0,1,117.39,96c-.18.27-.37.52-.57.77L96,121.45c7.49,15.22,23.41,31,38.83,38.51l24.34-20.71a8.12,8.12,0,0,1,.75-.56,16,16,0,0,1,15.17-1.4l.13.06,47.11,21.11A16,16,0,0,1,231.88,175.08Z'
                    fill='currentColor'
                    mask='url(#mentor-phone-slash)'
                  />
                  <line
                    x1='40'
                    y1='216'
                    x2='216'
                    y2='40'
                    stroke='currentColor'
                    strokeWidth='14'
                    strokeLinecap='round'
                  />
                </svg>
              </span>
              <span className={styles.mentor_calls_unavailable_copy}>
                <strong>Does not accept phone calls</strong>
                <small>You can still message {firstName} here.</small>
              </span>
            </div>
          </div>
        </section>
      )}

      {showCallCard && (
        <section
          className={styles.detail_call_card}
          data-callout-target={isMentor ? undefined : 'phone_feature_desktop'}
        >
          <div className={`${styles.detail_card_header} ${styles.detail_call_header}`}>
            <p>Calls</p>
          </div>
          <div className={styles.detail_call_actions}>
            {!isMentor && (
              <>
                <CallActionButton
                  variant='primary'
                  iconPreset='phone'
                  title='Request a call'
                  subtitle='Send a call request to your mentor'
                  onClick={handleRequestCall}
                  disabled={requestDisabled}
                />
                <CallActionButton
                  iconPreset='calendar'
                  title='Schedule a call'
                  subtitle='Choose a time that works for both of you'
                  onClick={handleScheduleCall}
                  disabled={scheduleDisabled}
                />
              </>
            )}
            {showSuggestCall && (
              <CallActionButton
                variant='primary'
                iconPreset='phone'
                title='Suggest a call'
                subtitle={
                  hasOpenSuggestion
                    ? `Suggestion sent — waiting on ${firstName}`
                    : `Invite ${firstName} to start or schedule a call`
                }
                onClick={handleSuggestCall}
                disabled={suggestDisabled}
              />
            )}
            <CallActionButton
              iconPreset='info'
              title='How calls work'
              subtitle='A guide to voice calls'
              onClick={handleHowCallsWork}
            />
          </div>
        </section>
      )}

      <RequestCallModal
        open={callModal === CALL_MODAL_REQUEST}
        otherUserFirstName={firstName}
        afterHoursRange={callHoursRange}
        callable={callable}
        conversationId={conversation?.uuid}
        incentive={callIncentive}
        onClose={closeCallModal}
        onCallCreated={handleCallCreated}
        onScheduleInstead={() => setCallModal(CALL_MODAL_SCHEDULE)}
      />

      <ScheduleCallModal
        open={callModal === CALL_MODAL_SCHEDULE}
        conversationId={conversation?.uuid}
        otherUserTimezone={otherUserTimezone}
        viewerTimezone={currentUser?.timezone}
        otherUserFirstName={firstName}
        otherUserName={displayName}
        otherUserPhoto={otherUserImageUrl}
        incentive={callIncentive}
        onClose={closeCallModal}
        onCallCreated={handleCallCreated}
      />
    </div>
  );
}
